import React, { useState, useEffect, useCallback } from 'react';
import api, { getCourses, loginUser } from '../api';
import './TeacherDashboard.css';

const TeacherDashboard = () => {
  // --- ESTADOS DE SEGURIDAD (ACTUALIZADO A JWT) ---
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [passInput, setPassInput] = useState('');
  const [authError, setAuthError] = useState('');

  // --- ESTADOS DEL DASHBOARD ---
  const [activeTab, setActiveTab] = useState('courses'); 
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState(''); 
  
  const [selectedCourse, setSelectedCourse] = useState(null); 
  
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false); 
  
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [editingCourseId, setEditingCourseId] = useState(null); 

  // ESTADO PARA LOS LINKS DEL CURSO
  const [courseResources, setCourseResources] = useState([]); 

  // ESTADO PARA LOS RECURSOS EXISTENTES DE UNA LECCIÓN
  const [existingLessonResources, setExistingLessonResources] = useState([]);

  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    short_description: '',
    category: 'mineria',
    is_free: false,
    price: 0,
    video_intro_url: '',
    card_image_url: '',
    instructor_id: 1 
  });

  const [newLesson, setNewLesson] = useState({
    title: '',
    subtitle: '',
    module_title: '', 
    video_url: '',
    image_url: '', 
    content: '',
    resource_link: '', 
    resource_title: ''
  });

  const [imageFile, setImageFile] = useState(null); 
  const [resourceFile, setResourceFile] = useState(null); 
  const [aiContextFile, setAiContextFile] = useState(null); 
  const [previewUrl, setPreviewUrl] = useState(null);

  const [studentForm, setStudentForm] = useState({
    full_name: '',
    email: '',
    dni: '',
    password: '',
    course_ids: []
  });

  const [isEditStudentModalOpen, setIsEditStudentModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState({
    id: null,
    full_name: '',
    email: '',
    dni: '',
    course_ids: []
  });

  // --- VERIFICAR SESIÓN GUARDADA ---
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    if (token && (role === 'admin' || role === 'founder')) {
      setIsAuthorized(true);
    }
  }, []);

  // --- NUEVO LOGIN SEGURO ---
  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const res = await loginUser({ email: emailInput, password: passInput });
      if (res.data.user_role === 'admin' || res.data.user_role === 'founder') {
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem('userName', res.data.user_name);
        localStorage.setItem('userRole', res.data.user_role);
        setIsAuthorized(true);
      } else {
        setAuthError('No tienes permisos de administrador.');
      }
    } catch (err) {
      setAuthError('Credenciales incorrectas.');
    }
  };

  // --- CERRAR SESIÓN DESDE EL PANEL DE CURSOS ---
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userRole');
    setIsAuthorized(false);
    window.location.reload();
  };

  // --- FUNCIONES DE CARGA DE DATOS ---
  const fetchCourses = useCallback(async () => {
    if (!isAuthorized) return;
    try {
      const response = await getCourses();
      setCourses(response.data);
      
      if (selectedCourse) {
        setSelectedCourse(prevSelected => {
            if (!prevSelected) return null;
            return response.data.find(c => c.id === prevSelected.id) || prevSelected;
        });
      }
    } catch (err) { 
      console.error("Error cargando cursos:", err); 
    }
  }, [selectedCourse, isAuthorized]);

  const fetchStudents = useCallback(async () => {
    if (!isAuthorized) return;
    try {
      const response = await api.get('/auth/students');
      setStudents(response.data);
    } catch (error) { 
      console.error("Error cargando alumnos:", error); 
    }
  }, [isAuthorized]);

  useEffect(() => { 
    if (isAuthorized) fetchCourses(); 
  }, [fetchCourses, isAuthorized]); 

  useEffect(() => { 
    if (isAuthorized && activeTab === 'students') {
      fetchStudents(); 
    }
  }, [activeTab, fetchStudents, isAuthorized]);

  // --- FUNCIONES AUXILIARES ---
  const uploadFileToSupabase = async (file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append("file", file);
    
    const res = await api.post('/upload/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.url;
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) return `https://www.youtube.com/embed/${match[2]}`;
    return url;
  };

  // --- GESTIÓN DE CURSOS ---
  const handleOpenCourseModal = (courseToEdit = null) => {
    if (courseToEdit) {
      setEditingCourseId(courseToEdit.id);
      
      // --- LÓGICA PARA EXTRAER LOS LINKS OCULTOS ---
      let desc = courseToEdit.description || '';
      let res = [];
      if (desc.includes('|||RECURSOS|||')) {
        const parts = desc.split('|||RECURSOS|||');
        desc = parts[0];
        try { res = JSON.parse(parts[1]); } catch(e){}
      }
      setCourseResources(res);
      // ---------------------------------------------

      setCourseForm({
        title: courseToEdit.title,
        description: desc,
        short_description: courseToEdit.short_description || '',
        category: courseToEdit.category,
        is_free: courseToEdit.is_free,
        price: courseToEdit.price || 0,
        video_intro_url: courseToEdit.video_intro_url || '',
        card_image_url: courseToEdit.card_image_url || '',
        instructor_id: courseToEdit.instructor_id || 1 
      });
      setPreviewUrl(courseToEdit.card_image_url);
    } else {
      setEditingCourseId(null);
      setCourseResources([]); 
      setCourseForm({
        title: '', description: '', short_description: '', category: 'mineria',
        is_free: false, price: 0, video_intro_url: '', card_image_url: '', instructor_id: 1 
      });
      setPreviewUrl(null);
    }
    setImageFile(null);
    setIsCourseModalOpen(true);
  };

  const handleSaveCourse = async (e) => {
    e.preventDefault();
    try {
      let finalImageUrl = courseForm.card_image_url || "https://via.placeholder.com/300";
      if (imageFile) {
        finalImageUrl = await uploadFileToSupabase(imageFile);
      }

      // --- LÓGICA PARA OCULTAR LOS LINKS EN LA DESCRIPCIÓN ---
      let finalDescription = courseForm.description;
      if (courseResources.length > 0) {
        finalDescription += '|||RECURSOS|||' + JSON.stringify(courseResources);
      }
      // -------------------------------------------------------

      const payload = {
        ...courseForm,
        description: finalDescription,
        video_intro_url: getEmbedUrl(courseForm.video_intro_url),
        card_image_url: finalImageUrl,
        price: parseFloat(courseForm.price),
        is_free: courseForm.is_free === 'true' || courseForm.is_free === true,
        instructor_id: parseInt(courseForm.instructor_id) 
      };

      if (editingCourseId) {
        await api.put(`/courses/${editingCourseId}`, payload);
        alert("Curso actualizado exitosamente.");
      } else {
        await api.post(`/courses/`, payload);
        alert("Curso creado exitosamente.");
      }

      setIsCourseModalOpen(false);
      fetchCourses();

    } catch (error) {
      console.error(error);
      alert("Error al guardar curso. Revisa la consola.");
    }
  };

  const handleDeleteCourse = async (courseId, e) => {
    e.stopPropagation();
    if (window.confirm("¿Estás SEGURO de eliminar este curso? Se borrarán todas sus lecciones y alumnos matriculados.")) {
      try {
        await api.delete(`/courses/${courseId}`);
        fetchCourses();
      } catch (error) {
        alert("Error al eliminar curso.");
      }
    }
  };

  // --- GESTIÓN DE LECCIONES ---
  const handleOpenLessonModal = () => {
    setEditingLessonId(null); 
    setExistingLessonResources([]);
    setNewLesson({ title: '', subtitle: '', module_title: '', video_url: '', image_url: '', content: '', resource_link: '', resource_title: '' });
    setPreviewUrl(null);
    setImageFile(null);
    setResourceFile(null);
    setAiContextFile(null);
    setIsLessonModalOpen(true);
  };

  const handleEditLesson = (lesson) => {
    setEditingLessonId(lesson.id); 
    setExistingLessonResources(lesson.resources ? lesson.resources.map(r => ({ ...r })) : []);
    setNewLesson({
      title: lesson.title,
      subtitle: lesson.subtitle || '',
      module_title: lesson.module_title || '',
      video_url: lesson.video_url || '',
      content: lesson.content || '',
      image_url: lesson.image_url || '',
      resource_link: '', 
      resource_title: '',
      order: lesson.order 
    });
    setPreviewUrl(lesson.image_url);
    setImageFile(null);
    setResourceFile(null);
    setAiContextFile(null);
    setIsLessonModalOpen(true);
  };
  
  const handleCloseLessonModal = () => {
    setIsLessonModalOpen(false);
    setEditingLessonId(null);
    setExistingLessonResources([]);
    setImageFile(null);
    setResourceFile(null);
    setAiContextFile(null);
    setPreviewUrl(null);
  };

  const handleExistingResourceChange = (index, field, value) => {
    const updated = [...existingLessonResources];
    updated[index][field] = value;
    setExistingLessonResources(updated);
  };

  const handleDeleteExistingResource = async (resourceId, resourceTitle) => {
    if (window.confirm(`¿Estás seguro de eliminar el recurso "${resourceTitle}"?`)) {
      try {
        await api.delete(`/courses/resources/${resourceId}`);
        setExistingLessonResources(prev => prev.filter(r => r.id !== resourceId));
        fetchCourses();
      } catch (error) {
        console.error(error);
        alert("Error al eliminar el recurso.");
      }
    }
  };

  const handleLessonInputChange = (e) => {
    setNewLesson({ ...newLesson, [e.target.name]: e.target.value });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleResourceFileSelect = (e) => {
    setResourceFile(e.target.files[0]);
  };

  const handleAiFileSelect = (e) => {
    setAiContextFile(e.target.files[0]);
  };

  const handleSaveLesson = async (e) => {
    e.preventDefault();
    if (!selectedCourse) return;

    try {
      let finalImageUrl = newLesson.image_url || "https://via.placeholder.com/300";
      if (imageFile) {
        finalImageUrl = await uploadFileToSupabase(imageFile);
      }

      const lessonPayload = {
        title: newLesson.title,
        subtitle: newLesson.subtitle,
        module_title: newLesson.module_title || 'Módulo 1. Introducción',
        video_url: getEmbedUrl(newLesson.video_url),
        content: newLesson.content,
        order: editingLessonId ? newLesson.order : (selectedCourse.lessons ? selectedCourse.lessons.length + 1 : 1),
        image_url: finalImageUrl
      };

      let targetLessonId;

      if (editingLessonId) {
        const res = await api.put(`/courses/lessons/${editingLessonId}`, lessonPayload);
        targetLessonId = res.data.id;

        // Actualizar recursos existentes si fueron modificados
        for (const resItem of existingLessonResources) {
          await api.put(`/courses/resources/${resItem.id}`, {
            title: resItem.title,
            file_url: resItem.file_url,
            resource_type: resItem.resource_type || 'link'
          });
        }

        alert("¡Lección actualizada!");
      } else {
        const res = await api.post(`/courses/${selectedCourse.id}/lessons`, lessonPayload);
        targetLessonId = res.data.id;
        alert("¡Lección creada!");
      }

      if (aiContextFile) {
        const aiFormData = new FormData();
        aiFormData.append("file", aiContextFile);
        await api.post(`/chat/upload-context/${targetLessonId}`, aiFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (resourceFile || newLesson.resource_link) {
        let resourceUrl = newLesson.resource_link;
        let resourceType = "link";

        if (resourceFile) {
          resourceUrl = await uploadFileToSupabase(resourceFile);
          if (resourceFile.name.endsWith('.pdf')) resourceType = 'pdf';
          else if (resourceFile.name.endsWith('.csv') || resourceFile.name.endsWith('.xlsx')) resourceType = 'excel';
          else if (resourceFile.name.endsWith('.zip') || resourceFile.name.endsWith('.rar')) resourceType = 'zip';
          else resourceType = 'code';
        }

        const resourcePayload = {
          title: newLesson.resource_title || (resourceFile ? resourceFile.name : "Recurso"),
          file_url: resourceUrl,
          resource_type: resourceType
        };
        await api.post(`/courses/lessons/${targetLessonId}/resources`, resourcePayload);
      }

      handleCloseLessonModal();
      fetchCourses();

    } catch (error) {
      console.error(error);
      alert("Error al guardar lección.");
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (window.confirm("¿Estás seguro de eliminar esta lección? No se puede deshacer.")) {
      try {
        await api.delete(`/courses/lessons/${lessonId}`);
        fetchCourses();
      } catch (error) {
        alert("Error al eliminar lección.");
      }
    }
  };

  // --- GESTIÓN DE ALUMNOS ---
  const handleStudentChange = (e) => {
    setStudentForm({ ...studentForm, [e.target.name]: e.target.value });
  };

  const handleCourseSelect = (e) => {
    const id = parseInt(e.target.value);
    const checked = e.target.checked;
    let newIds = [...studentForm.course_ids];
    if (checked) newIds.push(id); else newIds = newIds.filter(x => x !== id);
    setStudentForm({ ...studentForm, course_ids: newIds });
  };

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/create-student', studentForm);
      alert(`Alumno ${studentForm.full_name} registrado y matriculado exitosamente.`);
      setStudentForm({ full_name: '', email: '', dni: '', password: '', course_ids: [] });
      fetchStudents();
    } catch (error) {
      console.error(error);
      alert("Error al crear alumno. Revisa que el correo no esté duplicado.");
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (window.confirm("¿Estás seguro de eliminar a este alumno? Esta acción no se puede deshacer.")) {
      try {
        await api.delete(`/auth/students/${studentId}`);
        fetchStudents();
      } catch (error) { 
        alert("Error al eliminar"); 
      }
    }
  };

  const handleResetPassword = async (studentId, studentName) => {
    const newPassword = window.prompt(`Ingresa la NUEVA contraseña para el alumno ${studentName}:`);
    
    if (!newPassword) return; 

    if (newPassword.length < 3) {
      alert("La contraseña debe tener al menos 3 caracteres por seguridad.");
      return;
    }

    try {
      await api.put(`/auth/students/${studentId}/reset-password`, { new_password: newPassword });
      alert(`✅ Contraseña actualizada con éxito para ${studentName}. Ya puedes enviársela.`);
    } catch (error) {
      console.error(error);
      alert("❌ Error al actualizar la contraseña.");
    }
  };

  // --- EDICIÓN COMPLETA DEL PERFIL DEL ALUMNO ---
  const handleEditStudent = (student) => {
    const currentCourseIds = student.enrollments ? student.enrollments.map(e => e.course_id) : [];
    
    setEditingStudent({
      id: student.id,
      full_name: student.full_name,
      email: student.email,
      dni: student.dni || '',
      course_ids: currentCourseIds
    });
    setIsEditStudentModalOpen(true);
  };

  const handleEditStudentProfileChange = (e) => {
    setEditingStudent({ ...editingStudent, [e.target.name]: e.target.value });
  };

  const handleEditStudentCourseSelect = (e) => {
    const id = parseInt(e.target.value);
    const checked = e.target.checked;
    let newIds = [...editingStudent.course_ids];
    if (checked) {
      newIds.push(id);
    } else {
      newIds = newIds.filter(x => x !== id);
    }
    setEditingStudent({ ...editingStudent, course_ids: newIds });
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/auth/students/${editingStudent.id}/edit`, {
        full_name: editingStudent.full_name,
        email: editingStudent.email,
        dni: editingStudent.dni,
        course_ids: editingStudent.course_ids
      });
      alert(`✅ Perfil y accesos actualizados correctamente para ${editingStudent.full_name}.`);
      setIsEditStudentModalOpen(false);
      fetchStudents(); 
    } catch (error) {
      console.error(error);
      alert("❌ Error al actualizar el perfil del alumno.");
    }
  };

  // --- LÓGICA DE BÚSQUEDA ---
  const filteredStudents = students.filter(student => {
    const term = searchTerm.toLowerCase();
    const nameMatch = student.full_name ? student.full_name.toLowerCase().includes(term) : false;
    const dniMatch = student.dni ? student.dni.toLowerCase().includes(term) : false;
    return nameMatch || dniMatch;
  });


  // =====================================================================
  // RENDERIZADO DE PANTALLA DE BLOQUEO (Si no está autorizado)
  // =====================================================================
  if (!isAuthorized) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-box">
          <h2>Acceso Restringido</h2>
          <p>Ingrese sus credenciales de administrador o fundador.</p>
          <form onSubmit={handleAuthSubmit}>
            <input
              type="text"
              className="admin-login-input"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="Correo o Usuario"
              required
              autoFocus
            />
            <input
              type="password"
              className="admin-login-input"
              value={passInput}
              onChange={(e) => setPassInput(e.target.value)}
              placeholder="Contraseña"
              required
            />
            {authError && <p className="admin-error-msg">{authError}</p>}
            <button type="submit" className="admin-login-btn">Ingresar al Panel</button>
          </form>
        </div>
      </div>
    );
  }

  // =====================================================================
  // RENDERIZADO DEL DASHBOARD COMPLETO (Si está autorizado)
  // =====================================================================
  return (
    <div className="dashboard-container">
      
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <h3>Teacher Studio</h3>
        </div>
        <nav className="sidebar-nav" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <button 
            className={`nav-btn ${activeTab === 'courses' ? 'active' : ''}`}
            onClick={() => setActiveTab('courses')}
          >
            <i className="fas fa-book"></i> Mis Cursos
          </button>
          <button 
            className={`nav-btn ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            <i className="fas fa-users"></i> Alumnos
          </button>

          <button 
            className="nav-btn" 
            onClick={handleLogout}
            style={{ marginTop: 'auto', color: '#ff4d4d', borderTop: '1px solid #30363D', paddingTop: '15px' }}
          >
            <i className="fas fa-sign-out-alt"></i> Cerrar Sesión
          </button>
        </nav>
      </aside>

      <main className="dashboard-content">
        
        <header className="content-header">
          <h1>{activeTab === 'courses' ? 'Gestión de Contenido' : 'Gestión de Alumnos'}</h1>
          <div className="user-profile">
            <span>Admin BullMiner</span>
            <img src="https://randomuser.me/api/portraits/men/45.jpg" alt="Profile" />
            <button 
              onClick={handleLogout}
              style={{
                background: 'transparent',
                border: '1px solid #ff4d4d',
                color: '#ff4d4d',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <i className="fas fa-sign-out-alt"></i> Salir
            </button>
          </div>
        </header>

        {activeTab === 'courses' && (
          !selectedCourse ? (
            <div>
              <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px'}}>
                <h2>Mis Cursos y Webinars</h2>
                <button className="btn-save" onClick={() => handleOpenCourseModal(null)}>
                  + Nuevo Curso / Webinar
                </button>
              </div>
              
              <div className="courses-grid-dashboard">
                {courses.map(c => (
                  <div key={c.id} className="course-card-dash">
                    <div className={`status-indicator ${c.is_free ? 'free' : 'premium'}`}>{c.is_free ? 'Gratuito' : 'Premium'}</div>
                    
                    <button 
                      className="btn-delete-course" 
                      onClick={(e) => handleDeleteCourse(c.id, e)}
                      title="Eliminar Curso"
                    >
                      ×
                    </button>

                    <h3>{c.title}</h3>
                    <p>{c.lessons ? c.lessons.length : 0} Lecciones</p>
                    
                    <div style={{display:'flex', gap:'10px', marginTop:'15px'}}>
                      <button className="btn-manage" onClick={() => setSelectedCourse(c)}>
                        Ver Lecciones
                      </button>
                      <button className="btn-icon" onClick={() => handleOpenCourseModal(c)} title="Editar Curso">
                        <i className="fas fa-pen"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="course-editor">
              <button className="btn-back" onClick={() => setSelectedCourse(null)}>← Volver</button>
              
              <div className="editor-header">
                <h2>{selectedCourse.title}</h2>
                <button className="btn-add-lesson" onClick={handleOpenLessonModal}>
                  + Nueva Lección
                </button>
              </div>

              <div className="lessons-list-editor">
                {selectedCourse.lessons && selectedCourse.lessons.length > 0 ? (
                  selectedCourse.lessons.map((l, i) => (
                    <div key={l.id} className="lesson-row">
                      <div className="lesson-drag-handle">⋮⋮</div>
                      <img 
                        src={l.image_url || "https://via.placeholder.com/50"} 
                        alt="Thumb" 
                        style={{width: '50px', height: '30px', objectFit: 'cover', borderRadius: '4px'}} 
                      />
                      <div className="lesson-info-edit">
                        <strong>Lección {i + 1}: {l.title}</strong>
                        <span className="lesson-sub">{l.subtitle}</span>
                      </div>
                      <div className="lesson-actions">
                        <button className="btn-icon" onClick={() => handleEditLesson(l)} title="Editar"><i className="fas fa-pen"></i></button>
                        <button className="btn-icon delete" onClick={() => handleDeleteLesson(l.id)} title="Eliminar"><i className="fas fa-trash"></i></button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p style={{color: '#8B949E', textAlign: 'center', padding: '20px'}}>No hay lecciones creadas aún.</p>
                )}
              </div>
            </div>
          )
        )}

        {activeTab === 'students' && (
          <div className="students-manager">
            
            <div className="form-card-dash">
              <h2>Registrar Alumno</h2>
              <form onSubmit={handleCreateStudent}>
                <div className="form-row">
                  <div className="form-group-dash">
                    <label>Nombre Completo</label>
                    <input 
                      type="text" 
                      name="full_name" 
                      value={studentForm.full_name} 
                      onChange={handleStudentChange} 
                      required 
                    />
                  </div>
                  <div className="form-group-dash">
                    <label>Correo Electrónico</label>
                    <input 
                      type="email" 
                      name="email" 
                      value={studentForm.email} 
                      onChange={handleStudentChange} 
                      required 
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group-dash">
                    <label>Contraseña Temporal</label>
                    <input 
                      type="text" 
                      name="password" 
                      value={studentForm.password} 
                      onChange={handleStudentChange} 
                      required 
                    />
                  </div>
                  <div className="form-group-dash">
                    <label>DNI (Opcional)</label>
                    <input 
                      type="text" 
                      name="dni" 
                      value={studentForm.dni} 
                      onChange={handleStudentChange} 
                      placeholder="Ingrese DNI"
                    />
                  </div>
                </div>

                <div className="form-group-dash">
                  <label>Asignar Cursos</label>
                  <div className="checkbox-grid">
                    {courses.filter(c => !c.is_free).map(course => (
                      <label key={course.id} className="checkbox-label">
                        <input 
                          type="checkbox" 
                          value={course.id} 
                          onChange={handleCourseSelect}
                          checked={studentForm.course_ids.includes(course.id)}
                        />
                        {course.title}
                      </label>
                    ))}
                  </div>
                </div>

                <button type="submit" className="btn-save-student">Crear y Matricular</button>
              </form>
            </div>

            <div className="students-list-section">
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid #30363D', paddingBottom:'10px', marginBottom:'20px'}}>
                <h3 style={{margin:0, borderBottom:'none', paddingBottom:0}}>Directorio de Alumnos</h3>
              </div>
              
              {/* BARRA DE BÚSQUEDA */}
              <div className="search-bar-container">
                <i className="fas fa-search search-icon"></i>
                <input 
                  type="text" 
                  className="search-input" 
                  placeholder="Buscar por Nombre o DNI..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="table-responsive">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Fecha Registro</th>
                      <th>DNI</th>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Cursos</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.length > 0 ? (
                      filteredStudents.map(student => (
                        <tr key={student.id}>
                          <td>{student.created_at ? new Date(student.created_at).toLocaleDateString() : 'N/A'}</td>
                          <td>{student.dni ? student.dni : <span style={{color:'#8B949E', fontStyle:'italic'}}>No registrado</span>}</td>
                          <td>{student.full_name}</td>
                          <td>{student.email}</td>
                          <td>
                            <span className="badge-courses">
                              {student.enrollments ? student.enrollments.length : 0} Cursos
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <button 
                                className="btn-icon" 
                                onClick={() => handleEditStudent(student)}
                                title="Editar Perfil y Accesos"
                              >
                                <i className="fas fa-user-edit"></i>
                              </button>
                              <button 
                                className="btn-icon" 
                                onClick={() => handleResetPassword(student.id, student.full_name)}
                                title="Cambiar Contraseña"
                              >
                                <i className="fas fa-key"></i>
                              </button>
                              <button 
                                className="btn-icon delete" 
                                onClick={() => handleDeleteStudent(student.id)}
                                title="Dar de baja"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{textAlign: 'center', color: '#8B949E', padding: '30px'}}>
                          {searchTerm ? 'No se encontraron resultados para tu búsqueda.' : 'No hay alumnos registrados.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}

      </main>

      {/* MODAL DE CURSO */}
      {isCourseModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingCourseId ? 'Editar Curso' : 'Nuevo Curso'}</h3>
              <button onClick={() => setIsCourseModalOpen(false)} className="btn-close">×</button>
            </div>
            
            <form onSubmit={handleSaveCourse}>
              <div className="form-group-dash">
                <label>Título</label>
                <input type="text" value={courseForm.title} onChange={(e) => setCourseForm({...courseForm, title: e.target.value})} required />
              </div>
              <div className="form-group-dash">
                <label>Descripción Corta</label>
                <input type="text" value={courseForm.short_description} onChange={(e) => setCourseForm({...courseForm, short_description: e.target.value})} />
              </div>
              <div className="form-group-dash">
                <label>Descripción Completa</label>
                <textarea rows="3" value={courseForm.description} onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}></textarea>
              </div>
              
              {/* --- NUEVA SECCIÓN DE LINKS A DRIVE --- */}
              <div className="form-group-dash" style={{ borderTop: '1px solid #30363D', paddingTop: '20px', marginTop: '20px' }}>
                <label style={{ color: '#58A6FF' }}><i className="fas fa-link"></i> Materiales del Curso (Links a Drive/PDF)</label>
                <p style={{ fontSize: '0.8rem', color: '#8B949E', marginBottom: '15px' }}>Estos enlaces aparecerán en el panel lateral del curso.</p>
                
                {courseResources.map((res, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <input 
                      type="text" 
                      placeholder="Título (ej. Manual de Estimación)" 
                      value={res.title} 
                      onChange={(e) => {
                        const newRes = [...courseResources];
                        newRes[idx].title = e.target.value;
                        setCourseResources(newRes);
                      }} 
                      style={{ flex: 1 }}
                    />
                    <input 
                      type="text" 
                      placeholder="Link (https://drive...)" 
                      value={res.link} 
                      onChange={(e) => {
                        const newRes = [...courseResources];
                        newRes[idx].link = e.target.value;
                        setCourseResources(newRes);
                      }} 
                      style={{ flex: 2 }}
                    />
                    <button 
                      type="button" 
                      onClick={() => setCourseResources(courseResources.filter((_, i) => i !== idx))} 
                      className="btn-icon delete"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={() => setCourseResources([...courseResources, {title: '', link: ''}])} 
                  className="btn-save" 
                  style={{ backgroundColor: '#1F6FEB', marginTop: '5px' }}
                >
                  + Agregar Link
                </button>
              </div>
              {/* --- FIN NUEVA SECCIÓN --- */}

              <div className="form-row">
                <div className="form-group-dash">
                  <label>Categoría</label>
                  <select value={courseForm.category} onChange={(e) => setCourseForm({...courseForm, category: e.target.value})} style={{width:'100%', padding:'10px', background:'#0D1117', color:'white', border:'1px solid #30363D'}}>
                    <option value="mineria">Minería</option>
                    <option value="geologia">Geología</option>
                    <option value="python">Python</option>
                    <option value="ia">IA</option>
                  </select>
                </div>
                <div className="form-group-dash">
                  <label>Tipo</label>
                  <select value={courseForm.is_free} onChange={(e) => setCourseForm({...courseForm, is_free: e.target.value === 'true'})} style={{width:'100%', padding:'10px', background:'#0D1117', color:'white', border:'1px solid #30363D'}}>
                    <option value="false">Pago (Curso)</option>
                    <option value="true">Gratis (Webinar)</option>
                  </select>
                </div>
              </div>

              <div className="form-group-dash">
                <label>Instructor del Curso</label>
                <select 
                  value={courseForm.instructor_id} 
                  onChange={(e) => setCourseForm({...courseForm, instructor_id: e.target.value})} 
                  style={{width:'100%', padding:'10px', background:'#0D1117', color:'white', border:'1px solid #30363D'}}
                >
                  <option value="1">Ing. Maycol Benavides (Mike)</option>
                  <option value="2">Ing. Demian Gide</option>
                </select>
              </div>

              {courseForm.is_free && (
                <div className="form-group-dash">
                  <label>Link Video Webinar (YouTube)</label>
                  <input type="text" value={courseForm.video_intro_url} onChange={(e) => setCourseForm({...courseForm, video_intro_url: e.target.value})} placeholder="https://youtube.com/..." />
                </div>
              )}

              {!courseForm.is_free && (
                <div className="form-group-dash">
                  <label>Precio (USD)</label>
                  <input type="number" value={courseForm.price} onChange={(e) => setCourseForm({...courseForm, price: e.target.value})} />
                </div>
              )}

              <div className="form-group-dash">
                <label>Imagen de Portada</label>
                <div className="file-upload-wrapper">
                  <input type="file" id="course-img-upload" accept="image/*" onChange={handleImageSelect} className="file-upload-input" />
                  <label htmlFor="course-img-upload" className="file-upload-label">
                    {previewUrl ? <img src={previewUrl} alt="Preview" className="image-preview" /> : <div className="upload-placeholder"><i className="fas fa-cloud-upload-alt"></i><span>Subir imagen</span></div>}
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="submit" className="btn-save">Guardar Curso</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE LECCIÓN */}
      {isLessonModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingLessonId ? 'Editar Lección' : 'Nueva Lección'}</h3>
              <button onClick={handleCloseLessonModal} className="btn-close">×</button>
            </div>
            
            <form onSubmit={handleSaveLesson}>
              <div className="form-group-dash">
                <label>Título</label>
                <input 
                  type="text" 
                  name="title" 
                  value={newLesson.title} 
                  onChange={handleLessonInputChange} 
                  required 
                />
              </div>
              <div className="form-group-dash">
                <label>Subtítulo</label>
                <input 
                  type="text" 
                  name="subtitle" 
                  value={newLesson.subtitle} 
                  onChange={handleLessonInputChange} 
                />
              </div>

              <div className="form-group-dash">
                <label>Nombre del Módulo (Agrupador)</label>
                <input 
                  type="text" 
                  name="module_title" 
                  value={newLesson.module_title} 
                  onChange={handleLessonInputChange} 
                  placeholder="Ej: Módulo 1. FUNDAMENTOS DE GEOLOGÍA" 
                  required
                />
              </div>
              
              <div className="form-group-dash">
                <label>Imagen de Portada</label>
                <div className="file-upload-wrapper">
                  <input type="file" id="lesson-img-upload" accept="image/*" onChange={handleImageSelect} className="file-upload-input" />
                  <label htmlFor="lesson-img-upload" className="file-upload-label">
                    {previewUrl ? <img src={previewUrl} alt="Preview" className="image-preview" /> : <div className="upload-placeholder"><i className="fas fa-cloud-upload-alt"></i><span>Subir imagen</span></div>}
                  </label>
                </div>
              </div>

              <div className="form-group-dash">
                <label>Link YouTube (No Listado)</label>
                <input 
                  type="text" 
                  name="video_url" 
                  value={newLesson.video_url} 
                  onChange={handleLessonInputChange} 
                  placeholder="https://youtube.com/..." 
                />
              </div>

              <div className="form-group-dash">
                <label>Descripción / Notas</label>
                <textarea 
                  name="content" 
                  rows="3" 
                  value={newLesson.content} 
                  onChange={handleLessonInputChange}
                ></textarea>
              </div>
              
              <div className="resources-input-section">
                <h4><i className="fas fa-link"></i> Recursos para el Alumno</h4>

                {/* LISTA DE RECURSOS QUE YA TIENE LA LECCIÓN (EDITABLES Y ELIMINABLES) */}
                {existingLessonResources.length > 0 && (
                  <div style={{ marginBottom: '20px', borderBottom: '1px solid #30363D', paddingBottom: '15px' }}>
                    <label style={{ color: '#FFD700', fontSize: '0.85rem', marginBottom: '10px', display: 'block', fontWeight: 'bold' }}>
                      📂 Recursos actuales en esta clase ({existingLessonResources.length}):
                    </label>

                    {existingLessonResources.map((res, idx) => (
                      <div key={res.id} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={res.title}
                          onChange={(e) => handleExistingResourceChange(idx, 'title', e.target.value)}
                          placeholder="Título (ej: Basededatos)"
                          style={{ flex: 1, padding: '8px', background: '#161B22', border: '1px solid #30363D', color: 'white', borderRadius: '4px' }}
                        />
                        <input
                          type="text"
                          value={res.file_url}
                          onChange={(e) => handleExistingResourceChange(idx, 'file_url', e.target.value)}
                          placeholder="Link del recurso"
                          style={{ flex: 2, padding: '8px', background: '#161B22', border: '1px solid #30363D', color: '#58A6FF', borderRadius: '4px' }}
                        />
                        <a
                          href={res.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-icon"
                          title="Abrir link actual"
                          style={{ textDecoration: 'none' }}
                        >
                          <i className="fas fa-external-link-alt"></i>
                        </a>
                        <button
                          type="button"
                          className="btn-icon delete"
                          onClick={() => handleDeleteExistingResource(res.id, res.title)}
                          title="Eliminar este recurso"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    ))}
                    <small style={{ color: '#8B949E', display: 'block', marginTop: '5px' }}>
                      * Puedes corregir el nombre o link de cualquiera de arriba y darle a "Actualizar", o borrarlo con el basurero.
                    </small>
                  </div>
                )}

                <label style={{ color: '#2EA043', fontSize: '0.85rem', marginBottom: '10px', display: 'block', fontWeight: 'bold' }}>
                  ➕ Agregar un nuevo recurso adicional:
                </label>
                
                <div className="form-group-dash">
                  <label>Subir Archivo (PDF, Excel, Zip)</label>
                  <input type="file" onChange={handleResourceFileSelect} />
                </div>
                
                <div className="divider-dash">O usa un link externo</div>

                <div className="form-group-dash">
                  <label>Link de Descarga</label>
                  <input type="text" name="resource_link" value={newLesson.resource_link} onChange={handleLessonInputChange} placeholder="https://..." />
                </div>
                
                <div className="form-group-dash">
                  <label>Título del Recurso</label>
                  <input type="text" name="resource_title" value={newLesson.resource_title} onChange={handleLessonInputChange} placeholder="Ej: Manual.pdf" />
                </div>
              </div>

              <div className="resources-input-section" style={{borderColor: '#2EA043'}}>
                <h4 style={{color: '#2EA043'}}><i className="fas fa-robot"></i> Entrenamiento IA</h4>
                <div className="form-group-dash">
                  <label>Subir PDF de Contexto (Oculto)</label>
                  <input type="file" accept=".pdf" onChange={handleAiFileSelect} />
                  <small style={{color: '#8B949E'}}>El chatbot leerá este archivo para responder preguntas sobre esta clase.</small>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseLessonModal} className="btn-cancel">Cancelar</button>
                <button type="submit" className="btn-save">{editingLessonId ? 'Actualizar' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE EDICIÓN DE PERFIL Y ACCESOS DEL ALUMNO */}
      {isEditStudentModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Editar Perfil y Accesos</h3>
              <button onClick={() => setIsEditStudentModalOpen(false)} className="btn-close">×</button>
            </div>
            
            <form onSubmit={handleUpdateStudent}>
              
              <div className="form-group-dash">
                <label>Nombre Completo</label>
                <input 
                  type="text" 
                  name="full_name"
                  value={editingStudent.full_name} 
                  onChange={handleEditStudentProfileChange}
                  required 
                />
              </div>

              <div className="form-row">
                <div className="form-group-dash">
                  <label>Correo Electrónico</label>
                  <input 
                    type="email" 
                    name="email"
                    value={editingStudent.email} 
                    onChange={handleEditStudentProfileChange}
                    required 
                  />
                </div>
                <div className="form-group-dash">
                  <label>DNI</label>
                  <input 
                    type="text" 
                    name="dni"
                    value={editingStudent.dni} 
                    onChange={handleEditStudentProfileChange}
                    placeholder="Dejar vacío si no tiene"
                  />
                </div>
              </div>

              <hr style={{border: 'none', borderTop: '1px solid #30363D', margin: '20px 0'}} />

              <p style={{color: '#8B949E', marginBottom: '15px', fontSize: '0.9rem'}}>
                Marca o desmarca los cursos a los que este alumno tendrá acceso.
              </p>
              
              <div className="form-group-dash">
                <label>Cursos Disponibles</label>
                <div className="checkbox-grid">
                  {courses.filter(c => !c.is_free).map(course => (
                    <label key={course.id} className="checkbox-label">
                      <input 
                        type="checkbox" 
                        value={course.id} 
                        onChange={handleEditStudentCourseSelect}
                        checked={editingStudent.course_ids.includes(course.id)}
                      />
                      {course.title}
                    </label>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={() => setIsEditStudentModalOpen(false)} className="btn-cancel">Cancelar</button>
                <button type="submit" className="btn-save">Guardar Cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default TeacherDashboard;